import { integrationApiInstance } from "../../apis/integrationApi";
import {
  AUTHORITIES,
  ApprovalRules,
  Authority,
  AuthoritySet,
  AuthorizationPolicy,
  ConfigurableRequestType,
  DEFAULT_AUTHORIZATION_POLICY,
} from "./authorizationPolicy";
import { CustomerApprovers, toDirectoryUsers } from "./customerApprovers";

/** A customer that has approvers saved, with how many of each role. */
export interface CustomerApproverSummary {
  customerId: string;
  customerName: string;
  adminCount: number;
  hrCount: number;
}

/**
 * Loads and saves customer approvers. Shared by every agent, so they save on their own endpoints
 * rather than with the agent. Injected so the Authorizations step can be tested without Horizon.
 */
export interface AuthorizationSettingsStore {
  loadApproverSummary: () => Promise<CustomerApproverSummary[]>;
  loadApprovers: (customerId: string) => Promise<CustomerApprovers>;
  saveApprovers: (customerId: string, approvers: CustomerApprovers) => Promise<CustomerApprovers>;
}

const AUTHORITY_VALUES: readonly Authority[] = AUTHORITIES.map((authority) => authority.value);

/** Keeps known authorities in display order. An empty or unknown value falls back to the default row. */
export function authoritiesFromWire(value: unknown, fallback: AuthoritySet): AuthoritySet {
  const raw = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
  const chosen = AUTHORITY_VALUES.filter((authority) => raw.includes(authority));
  return chosen.length > 0 ? chosen : fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** Reads a server response; any missing or unknown field falls back to the default. */
export function policyFromWire(response: unknown): AuthorizationPolicy {
  const body = asRecord(response);
  const rules = asRecord(body.rules);
  const defaults = DEFAULT_AUTHORIZATION_POLICY;
  return {
    requireApproval: typeof body.require_approval === "boolean" ? body.require_approval : defaults.requireApproval,
    rules: (Object.keys(defaults.rules) as ConfigurableRequestType[]).reduce((parsed, key) => {
      parsed[key] = authoritiesFromWire(rules[key], defaults.rules[key]);
      return parsed;
    }, {} as ApprovalRules),
  };
}

/** An agent with no stored policy is not gated, so it reads as "Anyone can authorize". */
export function agentPolicyFromWire(stored: unknown): AuthorizationPolicy {
  return stored ? policyFromWire(stored) : { ...DEFAULT_AUTHORIZATION_POLICY, requireApproval: false };
}

export function policyToWire(policy: AuthorizationPolicy): { require_approval: boolean; rules: ApprovalRules } {
  return {
    require_approval: policy.requireApproval,
    rules: { ...policy.rules },
  };
}

export function approversFromWire(response: unknown): CustomerApprovers {
  const body = asRecord(response);
  return { admins: toDirectoryUsers(body.admins), hrContacts: toDirectoryUsers(body.hr_contacts) };
}

export function approverSummaryFromWire(response: unknown): CustomerApproverSummary[] {
  if (!Array.isArray(response)) return [];
  return response.flatMap((item) => {
    const row = asRecord(item);
    const customerId = row.customer_id === undefined || row.customer_id === null ? "" : String(row.customer_id);
    if (!customerId) return [];
    const count = (value: unknown) => (Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0);
    return [
      {
        customerId,
        customerName: typeof row.customer_name === "string" ? row.customer_name : `Customer ${customerId}`,
        adminCount: count(row.admin_count),
        hrCount: count(row.hr_count),
      },
    ];
  });
}

export function approversToWire(approvers: CustomerApprovers): object {
  return { admins: approvers.admins, hr_contacts: approvers.hrContacts };
}

export const horizonAuthorizationSettingsStore: AuthorizationSettingsStore = {
  loadApproverSummary: async () => approverSummaryFromWire(await integrationApiInstance.getCustomerApproverSummary()),
  loadApprovers: async (customerId) => approversFromWire(await integrationApiInstance.getCustomerAuthorizationApprovers(customerId)),
  saveApprovers: async (customerId, approvers) =>
    approversFromWire(await integrationApiInstance.saveCustomerAuthorizationApprovers(customerId, approversToWire(approvers))),
};
